import { QueryKey, useQuery } from "@tanstack/react-query";
import { ERRORS } from "../consts";
import { apiAdapter, fetcherFactory } from "../services";
import { useAuthStore, useLanguageStore } from "../stores";
import type { JavaBasicDate } from "../types";
import { getEnvironmentVariables } from "../utils/general";

const { basePath } = getEnvironmentVariables();

export function useAutotaggingKnowledgeQuery({
  enabled=true
}) {
  const accessToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const initiativeId = useAuthStore(state => state.userData?.initiative_id) ?? "";
  const corporateId = useAuthStore(state => state.userData?.organization_id) ?? "";
  const setUnauthorized = useAuthStore(state => state.setUnAuthorized);
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const fetcher = fetcherFactory();
  const isSeedingSession = useAuthStore(state => state.hasSeedSession);
  const { language } = useLanguageStore(state => state);

  const uri = `/learning-catalogue/filters/${corporateId}/available-knowledge`; // TO CHANGE WITH
  const q = new URLSearchParams({ langCode: language?.id??"" });
  return useQuery<
  unknown,
  unknown,
  {
    knowledge_area_learning_level?: string,
    obsolete: boolean,
    id: number,
    name: string,
    description: string,
    identifier: string,
    corporate_id: number,
    industries: number[],
    creation_date: JavaBasicDate,
    delete_date: JavaBasicDate,
    update_date: JavaBasicDate,
    created_by: number,
    updated_by: number,
    deleted_by: number,
    deleted_by_name: string,
    created_by_name: string,
    updated_by_name: string,
    children: {
      knowledge_domain_learning_level?: string,
      id: number,
      name: string,
      description: string,
      identifier: string,
      corporate_id: number,
      knowledge_area_id: number,
      creation_date: JavaBasicDate,
      delete_date: JavaBasicDate,
      update_date: JavaBasicDate,
      created_by: number,
      updated_by: number,
      deleted_by: number,
      deleted_by_name: string,
      created_by_name: string,
      updated_by_name: string,
    }[]
  }[],
  QueryKey
>({
  enabled: Boolean(enabled
    && accessToken
    && initiativeId
    && sessionToken
    && corporateId
    && language?.id
  ) && !isSeedingSession,
  queryFn: () => fetcher(
    `${basePath}${uri}?${q.toString()}`,
    {
      headers: {
        "Accept-Language": language?.id||"",
        "accept": "application/json",
        "Authorization": accessToken,
        "x-ada-session-token": sessionToken
      }
    })
    .then((res) => {
      return res.json();
    })
    .then((data: {
      obsolete: boolean,
      id: number,
      name: string,
      description: string,
      identifier: string,
      type: string,
      corporate_id: number,
      creation_date: JavaBasicDate,
      delete_date: JavaBasicDate,
      update_date: JavaBasicDate,
      created_by: number,
      updated_by: number,
      deleted_by: number,
      deleted_by_name: string,
      created_by_name: string,
      updated_by_name: string,
      capability_learning_level?: string,
    }[]) => {
      return apiAdapter(data);
    })
    .catch((error) => {
      if (error === ERRORS.UNAUTHORIZED) {
        setUnauthorized(true);
        throw error; // rethrow so that react query doesn't complain about undefined return value
      } else {
        throw error;
      }
    }),
  queryKey: [basePath, sessionToken, isSeedingSession, uri, language?.id]
});
}

export function useAutotaggingCapabilitiesQuery({
  enabled=true
}) {
  const accessToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const initiativeId = useAuthStore(state => state.userData?.initiative_id) ?? "";
  const corporateId = useAuthStore(state => state.userData?.organization_id) ?? "";
  const setUnauthorized = useAuthStore(state => state.setUnAuthorized);
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const fetcher = fetcherFactory();
  const isSeedingSession = useAuthStore(state => state.hasSeedSession);
  const { language } = useLanguageStore(state => state);

  const uri = `/learning-catalogue/filters/${corporateId}/available-capabilities`; // TO CHANGE WITH
  const q = new URLSearchParams({ langCode:language?.id??"" });
  return useQuery<
  unknown,
  unknown,
  {
      obsolete: boolean,
      id: number,
      name: string,
      description: string,
      identifier: string,
      type: "PROFESSIONAL" | "MANAGER",
      corporate_id: number,
      creation_date: JavaBasicDate,
      delete_date: JavaBasicDate,
      update_date: JavaBasicDate,
      created_by: number,
      updated_by: number,
      deleted_by: number,
      deleted_by_name: string,
      created_by_name: string,
      updated_by_name: string,
      capability_learning_level?: string,
    }[],
  QueryKey
>({
  enabled: Boolean(enabled
    && accessToken
    && initiativeId
    && sessionToken
    && corporateId
    && language?.id
  ) && !isSeedingSession,
  queryFn: () => fetcher(
    `${basePath}${uri}?${q.toString()}`,
    {
      headers: {
        "Accept-Language": language?.id||"",
        "accept": "application/json",
        "Authorization": accessToken,
        "x-ada-session-token": sessionToken
      }
    })
    .then((res) => {
      return res.json();
    })
    .then((data: {
      obsolete: boolean,
      id: number,
      name: string,
      description: string,
      identifier: string,
      type: "PROFESSIONAL" | "MANAGER",
      corporate_id: number,
      creation_date: JavaBasicDate,
      delete_date: JavaBasicDate,
      update_date: JavaBasicDate,
      created_by: number,
      updated_by: number,
      deleted_by: number,
      deleted_by_name: string,
      created_by_name: string,
      updated_by_name: string,
      capability_learning_level?: string,
    }[]) => {
      return apiAdapter(data);
    })
    .catch((error) => {
      if (error === ERRORS.UNAUTHORIZED) {
        setUnauthorized(true);
        throw error; // rethrow so that react query doesn't complain about undefined return value
      } else {
        throw error;
      }
    }),
  queryKey: [basePath, sessionToken, isSeedingSession, uri, language?.id]
});
}

export function useAutotaggingLevelsQuery({
  enabled=true
}) {
  const accessToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const initiativeId = useAuthStore(state => state.userData?.initiative_id) ?? "";
  const corporateId = useAuthStore(state => state.userData?.organization_id) ?? "";
  const setUnauthorized = useAuthStore(state => state.setUnAuthorized);
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const fetcher = fetcherFactory();
  const isSeedingSession = useAuthStore(state => state.hasSeedSession);
  const { language } = useLanguageStore(state => state);

  const uri = `/learning-catalogue/filters/${corporateId}/available-levels`; // TO CHANGE WITH
  const q = new URLSearchParams({ langCode:language?.id??"" });
  return useQuery<
  unknown,
  unknown,
  {
    id: number,
    identifier: string,
    corporate_id: number,
    name: string,
    description: string,
    sort: number,
    }[],
  QueryKey
>({
  enabled: Boolean(enabled
    && accessToken
    && initiativeId
    && sessionToken
    && corporateId
    && language?.id
  ) && !isSeedingSession,
  queryFn: () => fetcher(
    `${basePath}${uri}?${q.toString()}`,
    {
      headers: {
        "Accept-Language": language?.id||"",
        "accept": "application/json",
        "Authorization": accessToken,
        "x-ada-session-token": sessionToken
      }
    })
    .then((res) => {
      return res.json();
    })
    .then((data: {
    id: number,
    identifier: string,
    corporate_id: number,
    name: string,
    description: string,
    sort: number,
    }[]) => {
      return apiAdapter(data);
    })
    .catch((error) => {
      if (error === ERRORS.UNAUTHORIZED) {
        setUnauthorized(true);
        throw error; // rethrow so that react query doesn't complain about undefined return value
      } else {
        throw error;
      }
    }),
  queryKey: [basePath, sessionToken, isSeedingSession, uri, language?.id]
});
}
